import React from 'react';
import { motion } from 'framer-motion';
import FocusRing from '../components/FocusRing';
import OrbitCard from '../components/OrbitCard';
import GradeChart from '../components/GradeChart';
import { upcomingAssignments, courses, projectMilestones } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import { FiTrendingUp, FiActivity, FiZap, FiTarget, FiBox, FiChevronRight } from 'react-icons/fi';

const Dashboard = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-8 pb-12">
      <motion.div variants={itemVariants} className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-slate-400 text-sm font-medium mb-1 uppercase tracking-wider">Academic Cockpit</h2>
          <h1 className="text-3xl md:text-4xl font-bold">Welcome back, <span className="text-[#eaff00]">Scholar</span></h1>
        </div>
        <div className="flex items-center space-x-3">
          <div className="hidden md:flex items-center space-x-2 bg-[#15172b] px-4 py-2 rounded-full border border-[#2a2d4f]">
            <div className="w-2 h-2 rounded-full bg-[#eaff00] animate-pulse"></div>
            <span className="text-sm text-slate-300">Focus Mode Ready</span>
          </div>
          <button className="bg-[#eaff00] text-[#0b0c1b] px-5 py-2 rounded-full font-bold text-sm hover:scale-105 transition-transform flex items-center">
            <SafeIcon icon={FiZap} className="mr-2" /> Quick Action
          </button>
        </div>
      </motion.div>

      <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="glass-panel p-6 rounded-[2rem] flex items-center justify-between group hover:border-[#eaff00]/30 transition-all">
          <div>
            <p className="text-slate-400 text-sm mb-2 flex items-center"><SafeIcon icon={FiZap} className="mr-2"/> Today's Focus</p>
            <p className="text-3xl font-bold">4.5 <span className="text-lg text-slate-500 font-normal">hrs</span></p>
          </div>
          <FocusRing progress={75} size={80} strokeWidth={6} label="75%" sublabel="Goal" />
        </div>

        <div className="glass-panel p-6 rounded-[2rem] md:col-span-2 flex flex-col justify-between relative overflow-hidden group hover:border-[#eaff00]/30 transition-all">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#eaff00]/5 rounded-full blur-3xl group-hover:bg-[#eaff00]/10 transition-colors"></div>
          <div className="flex justify-between items-center mb-2 relative z-10">
            <p className="text-slate-400 text-sm flex items-center"><SafeIcon icon={FiTrendingUp} className="mr-2"/> GPA Trajectory</p>
            <div className="flex items-center">
               <span className="text-[#eaff00] font-bold text-xl mr-2">3.82</span>
               <span className="text-[10px] px-2 py-0.5 bg-[#eaff00]/20 text-[#eaff00] rounded-full">+0.12</span>
            </div>
          </div>
          <div className="h-32 w-full relative z-10">
             <GradeChart />
          </div>
        </div>
      </motion.div>

      <motion.div variants={itemVariants}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold flex items-center">
            <SafeIcon icon={FiActivity} className="mr-2 text-[#eaff00]" />
            Active Orbit <span className="ml-2 text-sm font-normal text-slate-500 hidden sm:inline">Upcoming Deadlines</span>
          </h3>
          <button className="text-sm text-[#eaff00] hover:underline flex items-center">
            View All <SafeIcon icon={FiChevronRight} className="ml-1" />
          </button>
        </div>
        
        <div className="flex space-x-4 overflow-x-auto pb-6 hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
          {upcomingAssignments.map(item => (
            <OrbitCard key={item.id} item={item} />
          ))}
        </div>
      </motion.div>

      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="glass-panel p-6 rounded-3xl lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <SafeIcon icon={FiBox} className="mr-2 text-[#eaff00]" />
            Course Telemetry
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {courses.map(course => (
              <div key={course.id} className="p-4 bg-[#0b0c1b]/50 rounded-2xl border border-[#2a2d4f] hover:border-[#eaff00]/30 transition-all cursor-pointer group">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-bold text-[#eaff00]">{course.code}</p>
                    <p className="text-xs text-slate-400">{course.name}</p>
                  </div>
                  <span className="text-xs font-bold px-2 py-1 bg-[#15172b] rounded-lg border border-[#2a2d4f]">{course.gradeGoal}</span>
                </div>
                <div className="flex items-center justify-between mt-auto">
                   <div className="flex -space-x-1">
                      {[1,2,3].map(i => <div key={i} className="w-5 h-5 rounded-full border border-[#0b0c1b] bg-slate-700 text-[8px] flex items-center justify-center">L</div>)}
                   </div>
                   <div className="text-right">
                     <p className="text-sm font-bold">{course.currentGrade}%</p>
                     <p className="text-[8px] text-slate-500 uppercase tracking-tighter">Current standing</p>
                   </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-panel p-6 rounded-3xl flex flex-col justify-center items-center text-center border-dashed border-[#2a2d4f] hover:border-[#eaff00]/30 transition-all cursor-pointer group relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-br from-[#eaff00]/0 to-[#eaff00]/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
           <div className="w-16 h-16 rounded-full bg-[#15172b] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform relative z-10 border border-[#2a2d4f]">
             <SafeIcon icon={FiZap} className="text-2xl text-[#eaff00]" />
           </div>
           <h4 className="font-semibold mb-2 relative z-10">Initialize Focus Protocol</h4>
           <p className="text-sm text-slate-400 max-w-[200px] relative z-10">Block distractions and enter a 90-minute hyper-focus state.</p>
           <div className="mt-6 flex space-x-2 relative z-10">
              <span className="text-[10px] px-2 py-1 bg-[#15172b] rounded text-slate-500">Lofi Radio</span>
              <span className="text-[10px] px-2 py-1 bg-[#15172b] rounded text-slate-500">DND On</span>
           </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Dashboard;